// components/Header.tsx
'use client';

import Image from 'next/image';
import NextLink from 'next/link';
import { useEffect, useState } from 'react';
import { useScrollContainer } from '@/app/_lib/providers/ScrollProvider';
import { ThemeAwareLogo } from './ThemeAwareLogo';

interface HeaderProps {
  isInnerPage?: boolean;
}

const Header = ({ isInnerPage = false }: HeaderProps) => {
  const appName = process.env.NEXT_PUBLIC_APP_NAME || '';
  const facilityTelephone = process.env.NEXT_PUBLIC_FACILITY_TELEPHONE || '';
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { scrollContainerRef } = useScrollContainer();

  // Determine initial scrolled state for inner pages
  useEffect(() => {
    if (isInnerPage) {
      setIsScrolled(true);
    }
  }, [isInnerPage]);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
    setIsMobileMenuOpen(false);
  };

  const handleToggleMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const removeActiveClass = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (!isInnerPage && scrollContainerRef.current) {
      // For non-inner pages, use scroll position relative to carousel height
      const handleScroll = () => {
        const scrollPosition = scrollContainerRef.current?.scrollTop || 0;
        const carousel = document.getElementById('hero-slideshow');
        const carouselHeight = carousel?.offsetHeight || 0;

        // Change header styling after scrolling past the carousel
        setIsScrolled(scrollPosition > carouselHeight);
      };

      // Add scroll event listener to the scroll container
      const scrollContainer = scrollContainerRef.current;
      scrollContainer.addEventListener('scroll', handleScroll);

      // Initial check
      handleScroll();

      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isInnerPage, scrollContainerRef]);

  useEffect(() => {
    // Always add resize listener
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array

  // Navigation items for reuse
  const navItems = [
    { id: 'about', label: 'About' },
    { id: 'services-and-ammenities', label: 'Services & Amenities' },
    { id: 'mission-and-values', label: 'Mission & Values' },
    { id: 'photo-gallery', label: 'Photos' },
    { id: 'waiver', label: 'ALW Program' },
    { id: 'contact-us', label: 'Contact' },
  ];

  const headerBgClass = 'bg-fuchsia-300/80 dark:bg-gray-900/90';
  const textColorClass = 'text-primary dark:text-gray-200';
  const phoneTextColorClass = 'text-primary hover:text-primary-dark dark:text-gray-200 dark:hover:text-white';
  const navOpacityClass = isScrolled || isInnerPage ? 'opacity-100' : 'opacity-95';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-2500 transition-all duration-300 ease-in-out ${headerBgClass} backdrop-blur-xl`}
    >
      {/* Top Banner Section */}
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='flex items-center justify-between h-20 md:h-24'>
          {/* Left Side: Logo */}
          <div className='flex w-full h-full py-2'>
            {/* Desktop Logo */}
            {isInnerPage ? (
              <NextLink
                href='/'
                aria-label='Return to homepage'
                className='cursor-pointer hidden md:flex items-center w-full h-full'
              >
                <ThemeAwareLogo />
              </NextLink>
            ) : (
              <button
                type='button'
                aria-label='Return to top of the page'
                onClick={scrollToTop}
                className='cursor-pointer hidden md:flex items-center w-full h-full'
              >
                <ThemeAwareLogo />
              </button>
            )}

            {/* Mobile Logo */}
            {isInnerPage ? (
              <NextLink href='/' className='flex md:hidden items-center cursor-pointer' aria-label='Return to homepage'>
                <div className='relative w-[55px] h-[50px] pr-2 aspect-971/892 flex items-center gap-y-1'>
                  <Image
                    src='/logo/new-flower-logo.svg'
                    height={163}
                    width={150}
                    alt={`${appName} flower logo`}
                    sizes='(max-width: 768px) 100vw, 150px'
                  />
                </div>
                <div>
                  <h2 className={`text-xl font-bold mb-0! pb-0! whitespace-nowrap font-serif ${textColorClass}`}>
                    {appName}
                  </h2>
                </div>
              </NextLink>
            ) : (
              <button
                type='button'
                className='flex md:hidden items-center cursor-pointer'
                aria-label='Return to top of the page'
                onClick={scrollToTop}
              >
                <div className='relative w-[55px] h-[50px] pr-2 aspect-971/892 flex items-center gap-y-1'>
                  <Image
                    src='/logo/new-flower-logo.svg'
                    height={163}
                    width={150}
                    alt={`${appName} flower logo`}
                    sizes='(max-width: 768px) 100vw, 150px'
                  />
                </div>
                <div>
                  <h2
                    className={`text-xl font-bold mb-0! pb-0! whitespace-nowrap font-serif ${textColorClass} text-shadow-lg/20`}
                  >
                    {appName}
                  </h2>
                </div>
              </button>
            )}
          </div>

          {/* Right Side: Theme Toggle, Phone & Mobile Menu Toggle */}
          <div className='flex items-center shrink-0 space-x-4'>
            {/* Desktop Phone Info */}
            <div className={'relative hidden md:flex flex-col items-end mr-6'}>
              <a
                href={`tel:${facilityTelephone.replace(/[^0-9]/g, '')}`}
                className={`text-3xl font-semibold font-serif ${phoneTextColorClass} transition-all duration-200`}
              >
                {facilityTelephone}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className='flex items-center md:hidden'>
              <button
                className={`inline-flex items-center justify-center p-2 rounded-md ${textColorClass} hover:text-white hover:bg-primary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white`}
                type='button'
                aria-controls='mobile-menu'
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? 'Close main menu' : 'Open main menu'}
                onClick={handleToggleMenu}
              >
                <span className='sr-only'>{isMobileMenuOpen ? 'Close main menu' : 'Open main menu'}</span>
                {/* Icon when menu is closed */}
                <svg
                  className={`${isMobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  aria-hidden={true}
                >
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M4 6h16M4 12h16M4 18h16' />
                </svg>
                {/* Icon when menu is open */}
                <svg
                  className={`${isMobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  aria-hidden={true}
                >
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Navigation */}
      <nav
        className={`hidden md:block bg-primary dark:bg-gray-800 shadow-md transition-all duration-300 ease-in-out ${navOpacityClass}`}
        aria-label='Main navigation'
      >
        <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-center items-center h-12'>
            <ul className='flex space-x-1 lg:space-x-2'>
              {navItems.map((item) => (
                <li key={item.id}>
                  {isInnerPage ? (
                    <NextLink
                      href={`/#${item.id}`}
                      className='text-white hover:text-accent dark:hover:text-primary-light px-3 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors duration-200 inline-block'
                      onClick={removeActiveClass}
                    >
                      {item.label}
                    </NextLink>
                  ) : (
                    <NextLink
                      href={`/#${item.id}`}
                      className='text-white hover:text-accent px-3 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors duration-200 inline-block'
                      onClick={removeActiveClass}
                    >
                      {item.label}
                    </NextLink>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Menu */}
      <div
        className={`${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
        } md:hidden bg-primary/95 dark:bg-gray-800/95 backdrop-blur-sm overflow-hidden transition-all duration-300 ease-in-out`}
        id='mobile-menu'
        aria-hidden={!isMobileMenuOpen}
      >
        <div className='px-2 pt-2 pb-3 space-y-1 sm:px-3'>
          <ul className='flex flex-col space-y-2' aria-label='Mobile navigation'>
            {navItems.map((item) => (
              <li key={item.id}>
                {isInnerPage ? (
                  <NextLink
                    href={`/#${item.id}`}
                    className='text-white hover:bg-white/10 dark:hover:bg-gray-700/50 block px-3 py-2 rounded-md text-base font-medium cursor-pointer transition-colors duration-200'
                    onClick={removeActiveClass}
                  >
                    {item.label}
                  </NextLink>
                ) : (
                  <NextLink
                    href={`/#${item.id}`}
                    className='text-white hover:bg-white/10 block px-3 py-2 rounded-md text-base font-medium cursor-pointer transition-colors duration-200'
                    onClick={removeActiveClass}
                    tabIndex={isMobileMenuOpen ? 0 : -1}
                  >
                    {item.label}
                  </NextLink>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </header>
  );
};

export default Header;
